<template>
  <teleport to="body">
    <transition name="quick-menu">
      <div v-if="isOpen" class="quick-menu-overlay" @mousedown.self="close">
        <div class="quick-menu-panel" role="dialog" aria-modal="true">
          <div class="quick-menu-search">
            <inline-svg class="quick-menu-search-icon" :src="require('@/assets/icons/search.svg')"/>
            <span v-if="activeCommand" class="quick-menu-command-chip">{{ $t(activeCommand.text) }}</span>
            <input
                ref="input"
                v-model="inputValue"
                type="text"
                :placeholder="argumentPlaceholder"
                @keydown.tab="onTab"
                @keydown.delete="onBackspace"
            />
            <span class="quick-menu-badge">esc</span>
          </div>

          <div class="quick-menu-list-wrap">
            <div class="quick-menu-shadow top" :class="{ 'is-visible': shadowTop }"></div>
            <div class="quick-menu-shadow bottom" :class="{ 'is-visible': shadowBottom }"></div>
            <div ref="list" class="quick-menu-list" @scroll="updateShadows">
            <template v-for="section in sections" :key="section.key">
              <div class="quick-menu-section">
                <p>{{ $t(section.title) }}</p>
                <button
                    v-if="section.key === 'recent'"
                    type="button"
                    class="quick-menu-section-action"
                    @click="clearRecent"
                >
                  {{ $t('quickMenu.clearRecent') }}
                </button>
              </div>
              <button
                  v-for="entry in section.entries"
                  :key="entry.uid"
                  ref="entries"
                  type="button"
                  class="quick-menu-entry"
                  :class="{ active: entry.uid === activeId }"
                  @mousemove="onEntryHover(entry, $event)"
                  @click="run(entry)"
              >
                <span class="quick-menu-entry-icon">
                  <inline-svg :src="require(`@/assets/icons/${entry.icon}`)"/>
                </span>
                <span class="quick-menu-entry-label">{{ entryLabel(entry) }}</span>
                <span class="quick-menu-entry-kind">{{ $t(entry.kind) }}</span>
              </button>
            </template>

              <template v-if="settingsRows.length">
                <div class="quick-menu-section">
                  <p>{{ $t('quickMenu.settingsFields') }}</p>
                  <span class="quick-menu-section-hint">{{ $t('quickMenu.hintTabSettings') }}</span>
                </div>
                <div ref="settingsList" @focusin="onSettingsFocusIn">
                <quick-setting-row
                    v-for="row in settingsRows"
                    :key="row.id"
                    :row="row"
                    :model-value="settingsValue(row)"
                    :source-options="sourceOptions(row)"
                    @update:model-value="onSettingsInput(row, $event)"
                    @open="openSettingsField"
                />
                </div>
              </template>

              <p v-if="isLoadingCommand" class="quick-menu-empty">{{ $t('generic.loadingData') }}</p>
              <p v-else-if="!flatEntries.length && !settingsRows.length" class="quick-menu-empty">{{ commandHint || $t('generic.noResult') }}</p>
            </div>
          </div>

          <div class="quick-menu-footer" :class="{ 'is-save': hasSettingsChanges }">
            <transition name="quick-menu-footer" mode="out-in">
              <div v-if="hasSettingsChanges" key="save" class="quick-menu-footer-content">
                <span><b>{{ saveModifierKey }}</b><b>↵</b> {{ $t('quickMenu.hintSave') }}</span>
                <design-button
                    class="quick-menu-save"
                    size="cell"
                    icon="save.svg"
                    :label="$t('generic.save')"
                    :hide-label-mobile="false"
                    :loading="editingOrganisation.loading"
                    @click="saveSettings"
                />
              </div>
              <div v-else key="nav" class="quick-menu-footer-content">
                <span><b>↑</b><b>↓</b> {{ $t('quickMenu.hintMove') }}</span>
                <span><b>↵</b> {{ $t('quickMenu.hintOpen') }}</span>
                <span><b>⇧</b><b>⇧</b> {{ $t('quickMenu.hintToggle') }}</span>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import {mapActions, mapState} from "vuex";
import {QuickMenuHelper} from "@/helpers/QuickMenuHelper";
import QuickSettingRow from "@/views/components/QuickSettingRow.vue";
import DesignButton from "@/views/components/DesignButton.vue";
import {buildSettingsFieldRows, searchSettingsFields} from "@/helpers/settingsFieldIndex";
import {applyShiftHoursRules} from "@/helpers/organisationPayload";
import {commandKeyLabel} from "@/helpers/platform";

const SETTINGS_RESULTS_MAX = 10;

const DOUBLE_TAP_DELAY = 400;
const RECENT_KEY = "quickMenu.recent";
const RECENT_MAX = 5;

function readRecent() {
    try {
        const raw = window.localStorage.getItem(RECENT_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
    } catch (error) {
        return [];
    }
}

const LAST_QUERY_KEY = "quickMenu.lastQuery";
const LAST_QUERY_TTL = 45000;

function readLastQuery() {
    try {
        const raw = window.localStorage.getItem(LAST_QUERY_KEY);
        if (!raw) return "";

        const saved = JSON.parse(raw);
        if (!saved?.value || Date.now() - saved.at > LAST_QUERY_TTL) return "";

        return saved.value;
    } catch (error) {
        return "";
    }
}

function writeLastQuery(value) {
    try {
        if (!value) {
            window.localStorage.removeItem(LAST_QUERY_KEY);
            return;
        }
        window.localStorage.setItem(LAST_QUERY_KEY, JSON.stringify({value, at: Date.now()}));
    } catch (error) {
        return;
    }
}

function writeRecent(ids) {
    try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(ids));
    } catch (error) {
        return;
    }
}

export default {
  name: "QuickMenu",
  components: {QuickSettingRow, DesignButton},
  data() {
    return {
      isOpen: false,
      settingsDraft: {},
      search: "",
      activeId: "",
      lastShiftAt: 0,
      recentIds: readRecent(),
      shadowTop: false,
      shadowBottom: false,
      isLoadingCommand: false,
      loadedSources: [],
      pointerX: -1,
      pointerY: -1
    };
  },
  computed: {
    ...mapState("account", ["user", "isLoggedIn"]),
    ...mapState("global", ["quickMenuRequest"]),
    ...mapState("organisation", ["organisation", "editingOrganisation"]),
    ...mapState("product", ["products"]),
    productOptions() {
      return [
        {value: "", text: this.$t('generic.none')},
        ...(this.products || []).map((product) => ({value: product.productId, text: product.name}))
      ];
    },
    canUseSettings() {
      return QuickMenuHelper.canUseSettings(this.user, this.isPhone());
    },
    settingsRows() {
      if (!this.search || this.parsedCommand || !this.canUseSettings) return [];
      return searchSettingsFields(this.settingsFieldRows, this.search, this.user, this.isPhone(), SETTINGS_RESULTS_MAX);
    },
    saveModifierKey() {
      return commandKeyLabel();
    },
    hasSettingsChanges() {
      return Object.keys(this.settingsDraft).length > 0;
    },
    allPages() {
      return QuickMenuHelper.pages(this.user, this.isPhone(), (key) => this.$t(key))
          .map((entry) => ({...entry, kind: "quickMenu.kindPage"}));
    },
    allActions() {
      return QuickMenuHelper.actions(this.user, this.isPhone())
          .map((entry) => ({...entry, kind: "quickMenu.kindAction"}));
    },
    inputValue: {
      get() {
        return this.parsedCommand ? this.parsedCommand.argument : this.search;
      },
      set(value) {
        this.search = this.parsedCommand
            ? `${this.parsedCommand.command.keyword}:${value}`
            : value;
      }
    },
    parsedCommand() {
      return QuickMenuHelper.parseCommand(this.search, this.user, this.isPhone());
    },
    activeCommand() {
      return this.parsedCommand?.command || null;
    },
    commandRows() {
      const source = this.activeCommand?.source;
      if (!source || source.user) return [];
      return this.$store.state[source.module]?.[source.state] || [];
    },
    orgIdQuery() {
      if (this.parsedCommand) return null;
      return QuickMenuHelper.parseOrgId(this.search, this.user, this.isPhone());
    },
    commandEntries() {
      const parsed = this.parsedCommand || this.orgIdQuery;
      if (!parsed) return [];

      return QuickMenuHelper.suggestions(parsed.command, parsed.argument, this.commandRows, this.user)
          .map((entry) => ({
            ...entry,
            id: `${parsed.command.id}:${entry.id}`,
            icon: parsed.command.icon,
            kind: "quickMenu.kindJump",
            command: parsed.command
          }));
    },
    argumentPlaceholder() {
      if (!this.activeCommand) return this.$t('quickMenu.placeholder');
      if (this.activeCommand.freeText) return this.$t('quickMenu.argumentTextPlaceholder');

      return this.activeCommand.idOnly
          ? this.$t('quickMenu.argumentIdPlaceholder')
          : this.$t('quickMenu.argumentPlaceholder');
    },
    commandHint() {
      const parsed = this.parsedCommand;
      if (!parsed) return "";

      const term = String(parsed.argument || "").trim();
      const minLength = parsed.command.minLength || 3;
      const isId = /^\d+$/.test(term);

      if (parsed.command.idOnly || parsed.command.freeText) {
        return this.$t(`quickMenu.ask${this.capitalize(parsed.command.id)}`);
      }
      if (term.length < (isId ? 1 : minLength)) {
        return this.$t(`quickMenu.ask${this.capitalize(parsed.command.id)}`);
      }
      return "";
    },
    allSettingsTabs() {
      return QuickMenuHelper.settingsTabs(this.user, this.isPhone(), (key) => this.$t(key))
          .map((entry) => ({...entry, kind: "quickMenu.kindPage"}));
    },
    allCommands() {
      return QuickMenuHelper.commandEntries(this.user, this.isPhone())
          .map((entry) => ({...entry, kind: "quickMenu.kindCommand"}));
    },
    allEntries() {
      return [...this.allPages, ...this.allSettingsTabs, ...this.allActions, ...this.allCommands];
    },
    recentEntries() {
      return this.recentIds
          .map((id) => this.allEntries.find((entry) => entry.id === id))
          .filter(Boolean);
    },
    sections() {
      const withUid = (key, entries) => entries.map((entry) => ({...entry, uid: `${key}:${entry.id}`}));
      const sections = [];

      if (!this.search) {
        sections.push({key: "recent", title: "quickMenu.recent", entries: withUid("recent", this.recentEntries)});
        sections.push({key: "pages", title: "quickMenu.goTo", entries: withUid("pages", this.allPages)});
        sections.push({key: "actions", title: "quickMenu.actions", entries: withUid("actions", this.allActions)});
        return sections.filter((section) => section.entries.length);
      }

      const jumps = withUid("jump", this.commandEntries);
      if (this.parsedCommand) {
        return jumps.length ? [{key: "jump", title: "quickMenu.jump", entries: jumps}] : [];
      }

      sections.push({key: "jump", title: "quickMenu.jump", entries: jumps});

      const ranked = this.allEntries
          .map((entry) => ({entry, ...QuickMenuHelper.rank(entry.label || this.$t(entry.text), entry.keywords, this.search)}))
          .filter((row) => row.score > 0)
          .sort((a, b) => b.score - a.score);

      const pick = (direct) => ranked.filter((row) => row.direct === direct).map((row) => row.entry);

      sections.push({key: "results", title: "quickMenu.results", entries: withUid("results", pick(true))});
      sections.push({key: "suggestions", title: "quickMenu.suggestions", entries: withUid("suggestions", pick(false))});
      return sections.filter((section) => section.entries.length);
    },
    flatEntries() {
      return this.sections.flatMap((section) => section.entries);
    },
    navEntries() {
      return [
        ...this.flatEntries,
        ...this.settingsRows.map((row) => ({uid: row.id, settingsRow: row}))
      ];
    },
    activeEntry() {
      return this.flatEntries.find((entry) => entry.uid === this.activeId) || null;
    }
  },
  methods: {
    ...mapActions("organisation", ["getOrganisation", "updateOrganisation"]),
    ...mapActions("product", ["ensureProducts"]),
    sourceOptions(row) {
      return row.source === "products" ? this.productOptions : [];
    },
    readPath(target, path) {
      return path.split(".").reduce((node, key) => (node == null ? node : node[key]), target);
    },
    writePath(target, path, value) {
      const keys = path.split(".");
      const last = keys.pop();
      const parent = keys.reduce((node, key) => {
        node[key] = {...(node[key] || {})};
        return node[key];
      }, target);
      parent[last] = value;
      return target;
    },
    settingsValue(row) {
      if (Object.prototype.hasOwnProperty.call(this.settingsDraft, row.field)) {
        return this.settingsDraft[row.field];
      }
      return this.readPath(this.organisation || {}, row.field);
    },
    isSameSettingsValue(row, value) {
      const original = this.readPath(this.organisation || {}, row.field);

      if (row.type === "boolean") return !!original === !!value;
      if (row.type === "number") {
        if (original == null || original === "") return value == null || value === "";
        return Number(original) === Number(value);
      }

      return String(original ?? "") === String(value ?? "");
    },
    onSettingsInput(row, value) {
      const draft = {...this.settingsDraft};

      if (this.isSameSettingsValue(row, value)) delete draft[row.field];
      else draft[row.field] = value;

      this.settingsDraft = draft;
    },
    openSettingsField(row) {
      this.close();
      this.$router.push({
        name: "MySettingsPage",
        params: {panel: String(row.tabIndex)},
        query: {search: row.label, focus: "1"}
      });
    },
    saveSettings() {
      if (!this.hasSettingsChanges || this.editingOrganisation.loading) return;

      const form = {...this.organisation};
      Object.entries(this.settingsDraft).forEach(([path, value]) => this.writePath(form, path, value));
      applyShiftHoursRules(form);

      this.updateOrganisation({organisation: form, orgId: this.user.orgId})
          .then(() => {
            this.settingsDraft = {};
            this.close();
          })
          .catch(() => {});
    },
    isSettingsFocused() {
      const list = this.$refs.settingsList;
      return !!list && list.contains(document.activeElement);
    },
    onKeydown(event) {
      if ((event.metaKey || event.ctrlKey) && (event.key === "k" || event.key === "K")) {
        if (!this.isLoggedIn) return;
        event.preventDefault();
        this.toggle();
        return;
      }
      if (this.isOpen) {
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && this.hasSettingsChanges) {
          event.preventDefault();
          this.saveSettings();
          return;
        }
        if (this.isSettingsFocused() && !["Escape", "ArrowDown", "ArrowUp"].includes(event.key)) {
          return;
        }
        if (document.activeElement?.tagName === "SELECT" && ["ArrowDown", "ArrowUp"].includes(event.key)) {
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          this.settingsDraft = {};

          if (this.search) {
            this.search = "";
            this.$nextTick(() => this.$refs.input?.focus());
            return;
          }
          this.close();
          return;
        }
        if (event.key === "ArrowDown") {
          event.preventDefault();
          this.move(1);
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          this.move(-1);
          return;
        }
        if (event.key === "Enter") {
          event.preventDefault();
          if (this.activeEntry) {
            this.run(this.activeEntry);
            return;
          }
          this.openCommandPage();
          return;
        }
      }
      if (event.key !== "Shift") {
        this.lastShiftAt = 0;
        return;
      }

      if (event.repeat || !this.isLoggedIn || this.isPhone()) return;

      const now = event.timeStamp;
      if (now - this.lastShiftAt < DOUBLE_TAP_DELAY) {
        this.lastShiftAt = 0;
        this.toggle();
        return;
      }
      this.lastShiftAt = now;
    },
    toggle() {
      if (this.isOpen) {
        this.close();
        return;
      }
      this.isOpen = true;
      this.search = readLastQuery();
      this.recentIds = readRecent();
      this.$nextTick(() => {
        const input = this.$refs.input;
        if (!input) return;
        input.focus();
        input.select();
      });
    },
    close() {
      if (!this.search) writeLastQuery("");
      this.isOpen = false;
    },
    openCommandPage() {
      const command = this.activeCommand;
      if (!command) return;

      const page = QuickMenuHelper.commandPage(command, this.user, this.isPhone());
      if (!page) return;

      this.rememberId(`command:${command.id}`);
      writeLastQuery(this.search);
      this.close();
      if (this.$route.fullPath !== page) this.$router.push(page);
    },
    onBackspace(event) {
      if (!this.parsedCommand || this.parsedCommand.argument) return;
      event.preventDefault();
      this.search = "";
    },
    capitalize(value) {
      return String(value || "").charAt(0).toUpperCase() + String(value || "").slice(1);
    },
    relatedCommand(entry) {
      if (!entry?.page || !entry.linkId) return null;
      const commands = QuickMenuHelper.commands(this.user, this.isPhone());
      if (entry.commandId) {
        return commands.find((command) => command.id === entry.commandId) || null;
      }
      return commands.find((command) => command.linkId === entry.linkId) || null;
    },
    onTab(event) {
      if (event.shiftKey) return;

      event.preventDefault();
      if (this.completeActive()) return;

      this.focusFirstSettingsControl();
    },
    focusFirstSettingsControl() {
      if (!this.settingsRows.length) return;

      this.activeId = this.settingsRows[0].id;
      this.focusSettingsRow(0);
    },
    onSettingsFocusIn(event) {
      const row = event.target.closest(".quick-setting-row");
      if (!row) return;

      row.scrollIntoView({block: "nearest"});

      const index = this.settingsRowElements().indexOf(row);
      if (index >= 0) this.activeId = this.settingsRows[index]?.id || "";
    },
    completeActive() {
      const entry = this.activeEntry || this.flatEntries[0];
      if (!entry || entry.command) return false;

      if (entry.commandKeyword) {
        const keyword = `${entry.commandKeyword}:`;
        if (keyword === this.search) return false;

        this.search = keyword;
        this.$nextTick(() => this.$refs.input?.focus());
        return true;
      }

      const command = this.relatedCommand(entry);
      const next = command ? `${command.keyword}:` : this.entryLabel(entry);
      if (!next || next === this.search) return false;

      this.search = next;
      this.$nextTick(() => this.$refs.input?.focus());
      return true;
    },
    ensureCommandData() {
      const command = this.activeCommand;
      const source = command?.source;
      if (!source || source.user || command.idOnly) return;

      const key = `${source.module}.${source.state}`;
      if (this.loadedSources.includes(key)) return;
      if (this.commandRows.length) {
        this.loadedSources.push(key);
        return;
      }

      this.loadedSources.push(key);
      this.isLoadingCommand = true;
      Promise.resolve(this.$store.dispatch(`${source.module}/${source.action}`))
          .catch(() => {})
          .then(() => {
            this.isLoadingCommand = false;
          });
    },
    entryLabel(entry) {
      if (entry.label) return entry.label;
      if (!entry.text) return "";

      const label = this.$t(entry.text);
      return entry.entityId ? `${label} ${entry.entityId}` : label;
    },
    onEntryHover(entry, event) {
      const moved = event.clientX !== this.pointerX || event.clientY !== this.pointerY;
      this.pointerX = event.clientX;
      this.pointerY = event.clientY;
      if (!moved) return;
      this.activeId = entry.uid;
    },
    move(step) {
      const entries = this.navEntries;
      if (!entries.length) return;
      const index = entries.findIndex((entry) => entry.uid === this.activeId);
      const next = (index + step + entries.length) % entries.length;
      this.activeId = entries[next].uid;
      this.applyActiveFocus();
    },
    applyActiveFocus() {
      const settingsIndex = this.settingsRows.findIndex((row) => row.id === this.activeId);

      if (settingsIndex < 0) {
        this.$refs.input?.focus();
        this.scrollToActive();
        return;
      }

      this.focusSettingsRow(settingsIndex);
    },
    settingsRowElements() {
      return Array.from(this.$refs.settingsList?.children || []);
    },
    focusSettingsRow(index) {
      this.$nextTick(() => {
        const row = this.settingsRowElements()[index];
        if (!row) return;

        row.scrollIntoView({block: "nearest"});
        row.querySelector("input, select, button")?.focus({preventScroll: true});
        this.updateShadows();
      });
    },
    scrollToActive() {
      this.$nextTick(() => {
        const index = this.flatEntries.findIndex((entry) => entry.uid === this.activeId);
        if (index < 0) return;
        const element = (this.$refs.entries || [])[index];
        element?.scrollIntoView({block: "nearest", behavior: "smooth"});
        this.updateShadows();
      });
    },
    updateShadows() {
      const element = this.$refs.list;
      if (!element) {
        this.shadowTop = false;
        this.shadowBottom = false;
        return;
      }
      const max = element.scrollHeight - element.clientHeight;
      this.shadowTop = element.scrollTop > 2;
      this.shadowBottom = element.scrollTop < max - 2;
    },
    clearRecent() {
      this.recentIds = [];
      writeRecent([]);
      writeLastQuery("");
      this.activeId = "";
      this.$nextTick(() => this.updateShadows());
    },
    remember(entry) {
      this.rememberId(entry.id);
    },
    rememberId(id) {
      const ids = [id, ...this.recentIds.filter((row) => row !== id)].slice(0, RECENT_MAX);
      this.recentIds = ids;
      writeRecent(ids);
    },
    run(entry) {
      if (!entry) return;

      if (entry.commandKeyword) {
        this.search = `${entry.commandKeyword}:`;
        this.$nextTick(() => this.$refs.input?.focus());
        return;
      }

      if (entry.command) {
        this.rememberId(`command:${entry.command.id}`);
        writeLastQuery(this.search);
        this.close();

        if (entry.command.switchOrg) {
          this.$store.dispatch("account/switchOrg", entry.entityId);
          return;
        }

        if (entry.command.query) {
          this.$router.push({
            name: entry.command.route,
            query: {...(entry.command.baseQuery || {}), [entry.command.query]: String(entry.entityId)}
          });
          return;
        }

        this.$router.push({
          name: entry.command.route,
          params: {[entry.command.param]: String(entry.entityId)}
        });
        return;
      }

      this.close();
      this.remember(entry);

      if (entry.module && entry.mutation) {
        this.$store.commit(`${entry.module}/${entry.mutation}`, entry.payload);
      }

      if (entry.page && this.$route.fullPath !== entry.page) this.$router.push(entry.page);
    }
  },
  created() {
    this.settingsFieldRows = buildSettingsFieldRows(String(this.$i18n.locale || "fr"));
  },
  watch: {
    quickMenuRequest() {
      if (!this.isLoggedIn) return;
      this.toggle();
    },
    search() {
      this.activeId = this.flatEntries[0]?.uid || "";
      this.ensureCommandData();
      this.$nextTick(() => this.updateShadows());
    },
    isOpen(open) {
      if (!open) {
        this.settingsDraft = {};
        return;
      }
      this.activeId = "";
      if (this.canUseSettings && !(this.organisation || {}).orgId) this.getOrganisation();
      this.ensureProducts();
      this.$nextTick(() => this.updateShadows());
    }
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
  }
};
</script>

<style lang="scss" scoped>
.quick-menu-overlay {
  position: fixed;
  inset: 0;
  z-index: $z-quick-menu;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(8, 10, 15, 0.65);
  backdrop-filter: blur(6px);
}

.quick-menu-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 560px;
  max-height: min(62vh, 560px);
  overflow: hidden;
  border-radius: $radius-default;
  border: 1px solid $color-separator;
  background: $color-card-back;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.quick-menu-search {
  position: relative;
  display: flex;
  align-items: center;
  gap: $default-edge-spacing-l;
  padding: 0 $default-edge-spacing-xl;
  border-bottom: 1px solid $color-separator;

  input {
    flex: 1 1 auto;
    min-width: 0;
    height: 58px;
    padding: 0;
    background: transparent;
    border-radius: 0;
    color: $color-white-text;
    font-size: $font-title;
  }
}

.quick-menu-search-icon {
  width: 18px;
  height: 18px;
  stroke: $color-secondary-text;
  fill: none;
}

.quick-menu-command-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  background: rgba($color-primary-blue, 0.18);
  color: $color-primary-blue;
  font-size: $font-small;
  font-weight: 500;
  white-space: nowrap;
}

.quick-menu-badge {
  padding: 3px 8px;
  border-radius: 6px;
  background: $color-input-back;
  color: $color-secondary-text;
  font-size: 11px;
  text-transform: uppercase;
}

.quick-menu-list-wrap {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
}

.quick-menu-shadow {
  position: absolute;
  left: 0;
  right: 0;
  height: 34px;
  z-index: 2;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;

  &.is-visible {
    opacity: 1;
  }

  &.top {
    top: 0;
    background: linear-gradient(to bottom,
        $color-card-back 0%,
        rgba($color-card-back, 0.85) 35%,
        rgba($color-card-back, 0) 100%);
  }

  &.bottom {
    bottom: 0;
    background: linear-gradient(to top,
        $color-card-back 0%,
        rgba($color-card-back, 0.85) 35%,
        rgba($color-card-back, 0) 100%);
  }
}

.quick-menu-list {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  overflow-y: auto;
  scroll-padding: 52px 0;
  scroll-behavior: smooth;
  padding: $default-edge-spacing 0 $default-edge-spacing-l;
  @include thin-scrollbar;
}

.quick-menu-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $default-edge-spacing-l;
  padding: $default-edge-spacing-l $default-edge-spacing-xl $default-edge-spacing;

  p {
    color: $color-secondary-text;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
}

.quick-menu-section-action {
  padding: 0;
  background: transparent;
  color: $color-secondary-text;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  transition: color 0.15s ease;

  &:hover {
    transform: none;
    opacity: 1;
    color: $color-danger;
  }
}

.quick-menu-entry {
  display: flex;
  align-items: center;
  gap: $default-edge-spacing-l;
  width: 100%;
  padding: 0 $default-edge-spacing-xl;
  height: 44px;
  background: transparent;
  color: $color-primary-text;
  text-align: left;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;

  &.active {
    background: $color-input-back;
    color: $color-white-text;
    transform: scale(0.98);

    .quick-menu-entry-icon {
      color: $color-primary-blue;
    }
  }
}

.quick-menu-entry-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  min-width: 18px;
  color: $color-secondary-text;
  transition: color 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  :deep(svg[stroke]:not([stroke="none"])),
  :deep(svg [stroke]:not([stroke="none"])) {
    stroke: currentColor;
  }

  :deep(svg[fill]:not([fill="none"])),
  :deep(svg [fill]:not([fill="none"])) {
    fill: currentColor;
  }
}

.quick-menu-entry-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quick-menu-entry-kind {
  color: $color-secondary-text;
  font-size: 11px;
}

.quick-menu-empty {
  padding: $default-edge-spacing-xxl;
  color: $color-secondary-text;
  text-align: center;
}

.quick-menu-footer {
  display: flex;
  align-items: center;
  height: 54px;
  flex: 0 0 54px;
  padding: 0 $default-edge-spacing-xl;
  border-top: 1px solid $color-separator;
  color: $color-secondary-text;
  font-size: 11px;

  span {
    white-space: nowrap;
  }

  b {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    margin-right: 4px;
    padding: 0 4px;
    border-radius: 5px;
    background: $color-input-back;
    color: $color-primary-text;
    font-weight: 500;
  }
}

.quick-menu-enter-active,
.quick-menu-leave-active {
  transition: opacity 0.18s ease;

  .quick-menu-panel {
    transition: transform 0.22s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.18s ease;
  }
}

.quick-menu-enter-from,
.quick-menu-leave-to {
  opacity: 0;

  .quick-menu-panel {
    opacity: 0;
    transform: translateY(-12px) scale(0.97);
  }
}

@media (prefers-reduced-motion: reduce) {
  .quick-menu-list {
    scroll-behavior: auto;
  }

  .quick-menu-enter-active,
  .quick-menu-leave-active,
  .quick-menu-enter-active .quick-menu-panel,
  .quick-menu-leave-active .quick-menu-panel {
    transition: none;
  }
}

.quick-menu-footer-content {
  display: flex;
  align-items: center;
  gap: $default-edge-spacing-xl;
  width: 100%;
}

.quick-menu-footer-enter-active,
.quick-menu-footer-leave-active {
  transition: transform 0.18s ease-out, opacity 0.18s ease-out;
}

.quick-menu-footer-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.quick-menu-footer-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.quick-menu-save {
  margin-left: auto;

  &:focus-visible {
    outline: none !important;
    box-shadow: 0 0 0 2px $color-card-back, 0 0 0 3px $color-primary-blue;
    animation: dialog-focus-ring-button 0.2s ease-out;
  }
}

.quick-menu-section-hint {
  margin-left: auto;
  color: $color-secondary-text;
  font-size: 11px;
}

@include mobile {
  .quick-menu-overlay {
    padding: 12px;
  }

  .quick-menu-footer {
    display: none;

    &.is-save {
      display: flex;

      span {
        display: none;
      }
    }
  }
}
</style>
